import React, { useState } from 'react';

interface GenerateReportProps {
  onGenerate: (type: 'hourly' | 'daily' | 'weekly' | 'monthly') => void;
  isGenerating: boolean;
}

const GenerateReport: React.FC<GenerateReportProps> = ({ onGenerate, isGenerating }) => {
  const [selectedType, setSelectedType] = useState<'hourly' | 'daily' | 'weekly' | 'monthly'>('daily');

  const reportTypes = [
    {
      type: 'hourly' as const,
      icon: '⏰',
      title: 'Hourly Report',
      description: 'Last hour transactions and metrics',
      period: 'Past 1 hour'
    },
    {
      type: 'daily' as const,
      icon: '📅',
      title: 'Daily Report',
      description: 'Last 24 hours comprehensive analysis',
      period: 'Past 24 hours'
    },
    {
      type: 'weekly' as const,
      icon: '📊',
      title: 'Weekly Report',
      description: 'Last 7 days summary and trends',
      period: 'Past 7 days'
    },
    {
      type: 'monthly' as const,
      icon: '📈',
      title: 'Monthly Report',
      description: 'Last 30 days detailed analysis',
      period: 'Past 30 days'
    }
  ];

  const handleGenerate = () => {
    onGenerate(selectedType);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Generate New Report</h3>
        <p className="text-sm text-gray-600">
          Create a comprehensive audit report for analysis and compliance
        </p>
      </div>

      {/* Report Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {reportTypes.map((type) => (
          <div
            key={type.type}
            className={`relative rounded-lg border-2 p-4 cursor-pointer transition-all ${
              selectedType === type.type
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedType(type.type)}
          >
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{type.icon}</span>
              <div className="flex-1">
                <h4 className={`text-sm font-medium ${
                  selectedType === type.type ? 'text-blue-900' : 'text-gray-900'
                }`}>
                  {type.title}
                </h4>
                <p className={`text-xs ${
                  selectedType === type.type ? 'text-blue-700' : 'text-gray-500'
                }`}>
                  {type.period}
                </p>
              </div>
            </div>
            <p className={`text-xs mt-2 ${
              selectedType === type.type ? 'text-blue-700' : 'text-gray-600'
            }`}>
              {type.description}
            </p>
            
            {/* Radio button indicator */}
            <div className={`absolute top-3 right-3 w-4 h-4 rounded-full border-2 ${
              selectedType === type.type
                ? 'border-blue-500 bg-blue-500'
                : 'border-gray-300'
            }`}>
              {selectedType === type.type && (
                <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Report Content Preview */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Report will include:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700">Transaction statistics</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700">Volume analysis</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700">Success/failure rates</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700">Processing times</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700">Validator performance</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700">Alert summary</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700">Chain health status</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700">Risk assessments</span>
            </div>
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <p>Report will be generated based on current system data</p>
          <p className="text-xs text-gray-500 mt-1">
            Estimated generation time: {selectedType === 'monthly' ? '2-5' : selectedType === 'weekly' ? '1-3' : '1-2'} minutes
          </p>
        </div>
        
        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white transition-colors ${
            isGenerating
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          }`}
        >
          {isGenerating ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Generating...
            </>
          ) : (
            <>
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Generate {selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} Report
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default GenerateReport; 